import { ReassignAttractionsDialog } from '@/components/attraction-types/ReassignAttractionsDialog';
import { AttractionsList } from '@/components/attractions';
import { attractionCrudConfig, type Attraction } from '@/config/objects/attractions';
import { useAttractionTypeContext } from '@/pages/attraction-types/useAttractionTypeContext';
import {
  Loading,
  useApiClient,
  type CrudConfig,
  type CrudListHeaderAction,
} from '@startupquickstart/frontend';
import { useQueryClient } from '@tanstack/react-query';
import { GitMerge } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from '@startupquickstart/frontend';

export function AttractionTypeAttractionsTab() {
  const { id } = useParams<{ id: string }>();
  const { attractionType, isLoading } = useAttractionTypeContext();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [showReassign, setShowReassign] = useState(false);

  const attractionTypeAttractionsConfig: CrudConfig<Attraction> = useMemo(
    () => ({
      ...attractionCrudConfig,
      entityName: 'Attraction',
      entityNamePlural: 'Attractions',
      endpoints: {
        ...attractionCrudConfig.endpoints,
        list: `/attraction-types/${id}/attractions`,
        delete: (attractionId: string) => `/attraction-types/${id}/attractions/${attractionId}`,
      },
      listView: {
        ...attractionCrudConfig.listView,
        actions: {
          add: {
            enabled: true,
            endpoint: '/attractions',
            where: attractionType
              ? {
                  types: {
                    none: {
                      attractionTypeId: attractionType.id,
                    },
                  },
                }
              : undefined,
            onAdd: async (attraction: Attraction) => {
              await apiClient.post(`/attraction-types/${id}/attractions`, {
                attractionId: attraction.id,
              });
              toast.success('Attraction added to attraction type');
            },
          },
          view: {
            enabled: true,
          },
          remove: {
            enabled: true,
            onRemove: async (attraction: Attraction) => {
              await apiClient.delete(`/attraction-types/${id}/attractions/${attraction.id}`);
              toast.success('Attraction removed from attraction type');
            },
          },
        },
      },
    }),
    [attractionType, id, apiClient],
  );

  const headerActions: CrudListHeaderAction[] = useMemo(
    () => [
      {
        key: 'reassign',
        label: 'Move to another type…',
        icon: GitMerge,
        variant: 'outline',
        onClick: () => setShowReassign(true),
      },
    ],
    [],
  );

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loading />
      </div>
    );
  }

  if (!id) return null;

  return (
    <>
      <AttractionsList
        config={attractionTypeAttractionsConfig}
        className="p-0"
        baseUrl="/attractions"
        headerActions={headerActions}
      />

      <ReassignAttractionsDialog
        open={showReassign}
        onOpenChange={setShowReassign}
        sourceTypeId={id}
        sourceName={attractionType?.name || 'this type'}
        onReassigned={() => {
          // Refresh the tab's list so moved attractions drop off immediately.
          queryClient.invalidateQueries({ queryKey: ['Attraction', 'list'] });
        }}
      />
    </>
  );
}
